import React, { useMemo } from "react";
import { IonModal, IonContent, IonHeader, IonToolbar, IonButtons, IonButton } from "@ionic/react";
import { IUser, ProgramData } from "@interfaces/Interfaces";
import { useRecoilState, useRecoilValueLoadable } from "recoil";
import { timelineEvidenceDraftAtom } from "@state/TimelineState";
import { userAtom } from "@state/State";
import * as FirebaseService from "@controllers/FirebaseService";
import { EventRegister } from "react-native-event-listeners";
import { HeaderTitle } from "parafolio-components";
import ContentContainer from "@components/common/ContentContainer";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import classNames from "classnames";
import { TRUST_BACKGROUND_COLOUR_VARIANTS } from "src/Constants";
import { useProgramData } from "@hooks/data/useProgramData";
import * as EvidenceUtils from "@utils/evidenceUtils";

type Props = {
  visible: boolean;
  closeModal: () => void;
};

const CanadaTimelineEvidenceModal: React.FC<Props> = (props) => {
  const { userPrograms } = useProgramData();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const [timelineEvidenceDraft, setTimelineEvidenceDraft] = useRecoilState(timelineEvidenceDraftAtom);

  const closeModal = () => {
    setTimelineEvidenceDraft(null);
    props.closeModal();
  };

  const handlePersonalEvidence = () => {
    if (!timelineEvidenceDraft) {
      return;
    }

    EventRegister.emit("evidence-modal/add-evidence-timeline", timelineEvidenceDraft);
    closeModal();
  };

  const handleProgram = (program: ProgramData) => {
    if (user.state !== "hasValue" || !user.contents || !timelineEvidenceDraft) {
      return;
    }

    const data = EvidenceUtils.buildTimelineProgramEvidenceDeeplink({
      program,
      user: user.contents,
      timelineEvidence: timelineEvidenceDraft,
    });

    FirebaseService.logEvent("add_program_evidence_pressed", {
      program: program.Title,
      source: "timeline_evidence_chooser",
    });

    EventRegister.emit("evidence/add-program-evidence", data);
    closeModal();
  };

  const programs = useMemo(() => {
    return [...userPrograms].sort((a, b) => (a.Title ?? "").localeCompare(b.Title ?? ""));
  }, [userPrograms]);

  return (
    <IonModal
      id="canada-timeline-evidence-modal"
      isOpen={props.visible}
      backdropDismiss
      canDismiss
      onDidDismiss={closeModal}
      className="fullscreen"
    >
      <IonHeader
        style={{
          height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
        }}
      >
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{ maxWidth: DataController.isWeb() ? 980 : undefined }}
        >
          <IonButtons slot="start">
            <IonButton
              mode="ios"
              className="cancelButton"
              onClick={closeModal}
            >
              {"Close"}
            </IonButton>
          </IonButtons>
          <HeaderTitle>{"Add Evidence"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ContentContainer className="!pt-3 pb-32">
          <div className="">
            {programs.map((program) => (
              <div key={program.ID}>
                <button
                  id={program.ID}
                  className="flex w-full items-center py-4 active:opacity-70"
                  onClick={() => handleProgram(program)}
                >
                  <div
                    className={classNames(
                      "flex shrink-0 items-center justify-center rounded px-1 py-0.5 text-13px font-semibold text-white",
                      TRUST_BACKGROUND_COLOUR_VARIANTS[program.TrustName!] ?? "!bg-NEAS"
                    )}
                  >
                    {program.TrustName ?? "XXX"}
                  </div>
                  <div className="ml-3 min-w-0 text-left text-16px font-semibold text-cta-blue">
                    {program.Title}
                  </div>
                </button>
                <div className="h-px w-full bg-grey-30" />
              </div>
            ))}
            <button
              className="flex w-full items-center px-4 py-3 active:opacity-70"
              onClick={handlePersonalEvidence}
            >
              <div className="text-16px font-semibold text-cta-blue">{"Personal evidence"}</div>
            </button>
          </div>
        </ContentContainer>
      </IonContent>
    </IonModal>
  );
};

export default CanadaTimelineEvidenceModal;
